"use client";

import { useRouter } from "next/navigation";
import { Phone, RefreshCw } from "lucide-react";
import type { NoNumberReason } from "@/app/_module/hooks/usePhoneNumberValidation";
import type { DialBlocker } from "@/app/_module/hooks/useDialBlocker";

// One source of truth for "you can't dial because you have no number" copy,
// shared by the dialer banner and the blocking dialog. Says WHY (trial ended,
// subscription ended, never bought) instead of leaving the agent to guess
// from a session that starts and stops.
export function noNumberCopy(reason: NoNumberReason, expired: { phoneNumber: string; expiredAt: string | null } | null) {
  const pretty = expired?.phoneNumber ? formatUs(expired.phoneNumber) : null;
  const when = expired?.expiredAt ? new Date(expired.expiredAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  switch (reason) {
    case "trial_expired":
      return {
        title: "Your free trial number has expired",
        body: `${pretty ? `Your trial number ${pretty}` : "Your trial number"}${when ? ` expired on ${when}` : " has expired"}. Calls can't go out without an active number. Get a number to keep dialing — it's live the moment you buy it.`,
        cta: "Get a number",
      };
    case "subscription_expired":
      return {
        title: "Your phone number subscription has ended",
        body: `${pretty ? `${pretty} is` : "Your number is"} no longer active. Reactivate it or buy a new one to keep dialing.`,
        cta: "Reactivate or buy a number",
      };
    case "never_purchased":
      return {
        title: "You don't have a phone number yet",
        body: "Calls go out from a number on your account, and there isn't one yet. Pick an area code and you can dial right away.",
        cta: "Get a number",
      };
    default:
      return {
        title: "No active phone number",
        body: "Calls can't go out without an active number on your account.",
        cta: "Get a number",
      };
  }
}

export function numbersSettingsPath(): string {
  const shell = typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  return shell ? "/m/settings/area-codes" : "/settings?tab=area-codes&areaCodeTab=purchase";
}

function formatUs(e164: string): string {
  const d = e164.replace(/\D/g, "");
  const n = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : e164;
}

/** Inline banner for the dialer panel. Renders nothing while the check is loading or when a number exists. */
export function NoActiveNumberNotice({
  reason,
  expired,
  onRecheck,
  rechecking,
}: {
  reason: NoNumberReason;
  expired: { phoneNumber: string; expiredAt: string | null } | null;
  onRecheck?: () => void;
  rechecking?: boolean;
}) {
  const router = useRouter();
  const copy = noNumberCopy(reason, expired);
  return (
    <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs flex items-start gap-2" role="status" data-testid="no-active-number-notice">
      <Phone className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
      <div className="flex-1 leading-snug">
        <div className="font-medium">{copy.title}</div>
        <div className="text-amber-800/90 mt-0.5">{copy.body}</div>
        <div className="mt-1.5 flex gap-1.5">
          <button
            type="button"
            onClick={() => router.push(numbersSettingsPath())}
            className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium transition-colors"
          >
            {copy.cta}
          </button>
          {onRecheck && (
            <button
              type="button"
              onClick={onRecheck}
              disabled={rechecking}
              className="px-2 py-1 bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 disabled:opacity-60"
            >
              <RefreshCw className={`h-3 w-3 ${rechecking ? "animate-spin" : ""}`} />
              I bought one
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Generic dialer banner for ANY blocker from useDialBlocker (payment, trial, subscription, number). */
export function DialBlockerNotice({ blocker, onRecheck, rechecking }: { blocker: DialBlocker; onRecheck?: () => void; rechecking?: boolean }) {
  const router = useRouter();
  const go = () => (blocker.href.startsWith("mailto:") ? (window.location.href = blocker.href) : router.push(blocker.href));
  const showRecheck = !!onRecheck && (blocker.kind === "no_number" || blocker.kind === "number_trial_expired" || blocker.kind === "number_subscription_expired");
  return (
    <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs flex items-start gap-2" role="status" data-testid="dial-blocker-notice" data-kind={blocker.kind}>
      <Phone className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
      <div className="flex-1 leading-snug">
        <div className="font-medium">{blocker.title}</div>
        <div className="text-amber-800/90 mt-0.5">{blocker.body}</div>
        <div className="mt-1.5 flex gap-1.5">
          <button type="button" onClick={go} className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium transition-colors">
            {blocker.cta}
          </button>
          {showRecheck && (
            <button
              type="button"
              onClick={onRecheck}
              disabled={rechecking}
              className="px-2 py-1 bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 disabled:opacity-60"
            >
              <RefreshCw className={`h-3 w-3 ${rechecking ? "animate-spin" : ""}`} />
              I bought one
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
